import { computed } from 'mobx';
import { Model } from 'core/model/Model';
import { ASSET_TAG_FILTER_PREFIX, AssetType, TagKeyUsage } from '@kentik/ui-shared/labels/lib';
import AssetTagValueCollection from './AssetTagValueCollection';

type AssetTagJson = TagKeyUsage & {
  user_id: string;
  color: string;
  values?: {
    del: Array<string | number>;
    set: ReturnType<AssetTagValueCollection['serialize']>;
  };
};

export default class AssetTagModel extends Model<AssetTagJson> {
  valueCollection = new AssetTagValueCollection([], { assetTagModel: this });

  get urlRoot() {
    return '/api/ui/assetTags/keys';
  }

  get urlPaths() {
    return {
      fetch: `/api/ui/assetTags/keys/${this.id}`,
      update: `/api/ui/assetTags/keys/${this.id}`
    };
  }

  get omitDuringSerialize(): Readonly<(keyof AssetTagJson)[]> {
    return ['usage', 'user_id'];
  }

  @computed
  get keyName(): string {
    return this.get('display_name') || this.get('name') || 'unknown';
  }

  /**
   * Provides a standard naming convention for tags we expose as admin sidebar filter fields.
   */
  @computed
  get filterName(): string {
    return `${ASSET_TAG_FILTER_PREFIX}${this.get('name')}`;
  }

  /**
   * Get a set of entity types that are using this asset tag.
   * @returns A set of entity types that are using this asset tag.
   */
  @computed
  get entityUsage(): Set<string> {
    return new Set(Object.keys(this.get('usage') || {}));
  }

  /**
   * Provides a lookup table for entity ids to their corresponding values.
   * @returns a hash keyed by entityType:entityId
   */
  @computed
  get tagLookup(): Record<string, string | number> {
    const lookups: Record<string, string | number> = {};
    const items = (this.get('items') || []) as NonNullable<TagKeyUsage['items']>;
    items.forEach(({ entity_type, entity_id, value }) => {
      lookups[`${entity_type}:${entity_id}`] = value;
    });
    return lookups;
  }

  /**
   * Get a set of entity identifiers that are tagged with this asset tag.
   * @returns A set of entity identifiers that are tagged with this asset tag.
   */
  @computed
  get taggedEntities(): Set<string> {
    return new Set(Object.entries(this.tagLookup).map(([key, value]) => `${key}:${value}`));
  }

  /**
   * Check if an entity is tagged with this asset tag.
   * @param options.relatedAssetModel - The entity model to check.
   * @param options.assetType - The type of the entity.
   * @param options.value - The value of the tag to check.
   * @returns A boolean indicating if the entity is tagged with this asset tag.
   */
  isTagged({
    relatedAssetModel,
    assetType,
    value
  }: {
    relatedAssetModel: { id: string | number };
    assetType: AssetType;
    value: string | number;
  }): boolean {
    return this.taggedEntities.has(`${assetType}:${relatedAssetModel.id}:${value}`);
  }

  /**
   * Returns the value for the given asset model and asset type.
   * @param options.relatedAssetModel - The entity model to check.
   * @param options.assetType - The type of the entity.
   * @returns The value of the tag for the specified entity and asset type, or undefined if not found.
   */
  getTagValue({
    relatedAssetModel,
    assetType
  }: {
    relatedAssetModel: { id: string | number };
    assetType: AssetType;
  }): string | number | undefined {
    return this.tagLookup[`${assetType}:${relatedAssetModel.id}`];
  }

  serialize(data?: Partial<AssetTagJson>) {
    const { removedIds } = this.valueCollection;

    return super.serialize({
      ...(data || this.get()),
      values: {
        del: Array.from(removedIds),
        set: this.valueCollection.serialize()
      }
    });
  }
}
